export type WorkingRateUnit = 'm2' | 'mb' | 'szt' | 'komplet' | 'percent';

export type WorkingRateStatus = 'active' | 'advisory' | 'backlog';

export type WorkingRateCategory =
  | 'malarz'
  | 'plytki'
  | 'lazienka'
  | 'hydraulika_montaz'
  | 'przygotowanie'
  | 'gk_murowanie'
  | 'bruk';

export type WorkingRateRegion = 'olsztyn_warmia';

export type WorkingRateItem = {
  id: string;
  category: WorkingRateCategory;
  label: string;
  unit: WorkingRateUnit;
  laborRateNet?: number;
  helperMaterialRateNet?: number;
  rangeMin?: number;
  rangeMax?: number;
  region: WorkingRateRegion;
  status: WorkingRateStatus;
  description: string;
};

export type QuickWorkingRateScope =
  | 'painting'
  | 'plaster_painting'
  | 'scrape_prime_plaster_paint'
  | 'panels'
  | 'bathroom'
  | 'paving'
  | 'other'
  | string;

const REGION: WorkingRateRegion = 'olsztyn_warmia';

export const WORKING_RATE_BOOK_VERSION = 'V1_OLSZTYN_WARMIA';

export const WORKING_RATE_BOOK_V1: WorkingRateItem[] = [
  {
    id: 'malowanie_2x',
    category: 'malarz',
    label: 'Malowanie 2x',
    unit: 'm2',
    laborRateNet: 28,
    rangeMin: 25,
    rangeMax: 35,
    region: REGION,
    status: 'active',
    description: 'Robocizna malowania dwukrotnego. Grunt, zabezpieczenia i materialy potwierdza wykonawca.',
  },
  {
    id: 'gladz_obrobka_szlif',
    category: 'malarz',
    label: 'Gladź z obrobka i szlifowaniem',
    unit: 'm2',
    laborRateNet: 55,
    rangeMin: 55,
    rangeMax: 65,
    region: REGION,
    status: 'active',
    description: 'Jedna kompleksowa pozycja: nakladanie, obrobka, szlifowanie i odpylanie.',
  },
  {
    id: 'gladz_malowanie',
    category: 'malarz',
    label: 'Gladź + malowanie',
    unit: 'm2',
    laborRateNet: 83,
    rangeMin: 75,
    rangeMax: 95,
    region: REGION,
    status: 'active',
    description: 'Pakiet robocizny: gladź z obrobka i szlifowaniem oraz malowanie 2x. Materialy osobno.',
  },
  {
    id: 'materialy_pomocnicze_malarz',
    category: 'malarz',
    label: 'Materialy pomocnicze malarza',
    unit: 'percent',
    rangeMin: 15,
    rangeMax: 25,
    region: REGION,
    status: 'advisory',
    description: 'Walki, kuwety, papiery scierne, folie, tasmy i drobne zuzywki jako procent robocizny.',
  },
  {
    id: 'material_glowny_standard',
    category: 'malarz',
    label: 'Material glowny standard',
    unit: 'percent',
    rangeMin: 20,
    rangeMax: 30,
    region: REGION,
    status: 'advisory',
    description: 'Orientacyjny koszt farby, gruntu lub gladzi standardowej, jesli material jest po stronie wykonawcy.',
  },
  {
    id: 'material_glowny_premium',
    category: 'malarz',
    label: 'Material glowny premium',
    unit: 'percent',
    rangeMin: 45,
    rangeMax: 60,
    region: REGION,
    status: 'advisory',
    description: 'Material premium moze istotnie zmienic koszt materialow, ale nie dolicza sie bez decyzji wykonawcy.',
  },
  {
    id: 'zabezpieczenie_folia_tasma',
    category: 'malarz',
    label: 'Zabezpieczenie folia i tasma',
    unit: 'm2',
    rangeMin: 3,
    rangeMax: 6,
    region: REGION,
    status: 'advisory',
    description: 'Folie, tasmy, zabezpieczenie podlog, listew i okien. Pozycja do decyzji wykonawcy.',
  },
  {
    id: 'akrylowanie_polaczen',
    category: 'malarz',
    label: 'Akrylowanie polaczen',
    unit: 'mb',
    rangeMin: 6,
    rangeMax: 12,
    region: REGION,
    status: 'advisory',
    description: 'Polaczenia przy listwach, naroznikach i sufitach warto potwierdzic przed oferta.',
  },
  {
    id: 'naprawa_ubytkow_malarz',
    category: 'malarz',
    label: 'Naprawa ubytkow pod malowanie',
    unit: 'm2',
    rangeMin: 10,
    rangeMax: 25,
    region: REGION,
    status: 'advisory',
    description: 'Drobne naprawy podloza nie powinny znikac w samej stawce malowania.',
  },
  {
    id: 'malowanie_sufitu',
    category: 'malarz',
    label: 'Malowanie sufitu',
    unit: 'm2',
    rangeMin: 30,
    rangeMax: 45,
    region: REGION,
    status: 'advisory',
    description: 'Sufit czesto ma inna pracochlonnosc niz sciany. Wykonawca zatwierdza stawke.',
  },
  {
    id: 'praca_w_umeblowanym_lokalu',
    category: 'malarz',
    label: 'Praca w umeblowanym lokalu',
    unit: 'percent',
    rangeMin: 10,
    rangeMax: 20,
    region: REGION,
    status: 'advisory',
    description: 'Przenoszenie, zabezpieczenia i wolniejsza praca moga podniesc koszt robocizny.',
  },
  {
    id: 'wysokosc_powyzej_28',
    category: 'malarz',
    label: 'Wysokosc powyzej 2.8 m',
    unit: 'percent',
    rangeMin: 10,
    rangeMax: 25,
    region: REGION,
    status: 'advisory',
    description: 'Drabiny, podesty i trudniejszy dostep wymagaja osobnej decyzji.',
  },
  {
    id: 'gres_standard',
    category: 'plytki',
    label: 'Gres standard',
    unit: 'm2',
    laborRateNet: 150,
    rangeMin: 130,
    rangeMax: 175,
    region: REGION,
    status: 'active',
    description: 'Orientacyjna robocizna dla standardowego formatu i typowego ukladu.',
  },
  {
    id: 'gres_wielkoformatowy',
    category: 'plytki',
    label: 'Gres wielkoformatowy',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Wycena indywidualna. Duzy format zwykle oznacza wyzsza trudnosc, wiecej osob i ryzyko uszkodzen.',
  },
  {
    id: 'mozaika',
    category: 'plytki',
    label: 'Mozaika',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Wymaga osobnej decyzji wykonawcy ze wzgledu na czas docinek i precyzje.',
  },
  {
    id: 'klinkier',
    category: 'plytki',
    label: 'Klinkier',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Zakres do potwierdzenia: format, fuga, narozniki i przygotowanie podloza.',
  },
  {
    id: 'kamien_naturalny',
    category: 'plytki',
    label: 'Kamien naturalny',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Wycena indywidualna. Material, impregnacja i ryzyko uszkodzen wymagaja potwierdzenia.',
  },
  {
    id: 'jolly_45',
    category: 'plytki',
    label: 'Jolly 45 stopni',
    unit: 'mb',
    rangeMin: 80,
    rangeMax: 120,
    region: REGION,
    status: 'advisory',
    description: 'Docinki i szlify naroznikow licz jako osobna pozycje do decyzji wykonawcy.',
  },
  {
    id: 'otwor_w_gresie',
    category: 'plytki',
    label: 'Otwor w gresie',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Otwory pod baterie, gniazda lub przepusty licz osobno, szczegolnie w twardym gresie.',
  },
  {
    id: 'system_poziomowania',
    category: 'plytki',
    label: 'System poziomowania',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Kliny i klipsy traktuj jako material pomocniczy do potwierdzenia.',
  },
  {
    id: 'tarcze_diamentowe',
    category: 'plytki',
    label: 'Tarcze diamentowe',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Zuzywki do ciecia nie powinny znikac z roboczej kalkulacji wykonawcy.',
  },
  {
    id: 'fugowanie',
    category: 'plytki',
    label: 'Fugowanie',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Fuga, kolor, szerokosc spoiny i mycie po fugowaniu wymagaja potwierdzenia.',
  },
  {
    id: 'silikonowanie_lazienka',
    category: 'plytki',
    label: 'Silikonowanie',
    unit: 'mb',
    rangeMin: 12,
    rangeMax: 25,
    region: REGION,
    status: 'advisory',
    description: 'Naroza, styk wanna/sciana i kabina powinny byc osobna pozycja do sprawdzenia.',
  },
  {
    id: 'listwy_narozne_plytki',
    category: 'plytki',
    label: 'Listwy narozne do plytek',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Material i montaz listew ustala wykonawca z klientem.',
  },
  {
    id: 'uklad_dekoracyjny_plytki',
    category: 'plytki',
    label: 'Uklad dekoracyjny / wzor',
    unit: 'percent',
    rangeMin: 10,
    rangeMax: 30,
    region: REGION,
    status: 'advisory',
    description: 'Wzory, przesuniecia i docinki moga podniesc czas pracy.',
  },
  {
    id: 'docinki_plytki',
    category: 'plytki',
    label: 'Docinki plytek',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Dlugie serie docinek, wneki i narozniki warto potwierdzic oddzielnie.',
  },
  {
    id: 'klej_elastyczny',
    category: 'plytki',
    label: 'Klej elastyczny / specjalny',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Material pomocniczy zalezy od formatu plytki, podloza i systemu.',
  },
  {
    id: 'lazienka_deweloperska',
    category: 'lazienka',
    label: 'Lazienka deweloperska',
    unit: 'm2',
    rangeMin: 1200,
    rangeMax: 1450,
    region: REGION,
    status: 'advisory',
    description: 'Orientacyjnie od m2 podlogi. Zalezy od projektu, punktow i formatu plytek.',
  },
  {
    id: 'lazienka_generalny_remont',
    category: 'lazienka',
    label: 'Lazienka generalny remont',
    unit: 'm2',
    rangeMin: 3500,
    rangeMax: 4500,
    region: REGION,
    status: 'advisory',
    description: 'Orientacyjny zakres od m2 podlogi przy demontazu, instalacjach i pelnym wykonczeniu.',
  },
  {
    id: 'mala_lazienka_komplet',
    category: 'lazienka',
    label: 'Mala lazienka komplet',
    unit: 'komplet',
    rangeMin: 12000,
    rangeMax: 20000,
    region: REGION,
    status: 'advisory',
    description: 'Male lazienki czesto liczy sie kwotowo, bo detale i ciasnota podbijaja czas pracy.',
  },
  {
    id: 'lazienka_mikro_detale',
    category: 'lazienka',
    label: 'Mala lazienka - duzo detali',
    unit: 'percent',
    rangeMin: 10,
    rangeMax: 25,
    region: REGION,
    status: 'advisory',
    description: 'Mala powierzchnia nie oznacza malej pracochlonnosci. Detale moga podbic koszt.',
  },
  {
    id: 'prysznic_walk_in',
    category: 'lazienka',
    label: 'Prysznic walk-in',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Spadki, hydroizolacja, odpływ i szyba wymagaja osobnego potwierdzenia.',
  },
  {
    id: 'zabudowa_wanny',
    category: 'lazienka',
    label: 'Zabudowa wanny',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Zakres zalezy od konstrukcji, plytek, rewizji i uszczelnien.',
  },
  {
    id: 'punkt_hydrauliczny',
    category: 'hydraulika_montaz',
    label: 'Punkt hydrauliczny',
    unit: 'szt',
    laborRateNet: 250,
    helperMaterialRateNet: 150,
    region: REGION,
    status: 'active',
    description: 'Robocizna punktu plus material pomocniczy do potwierdzenia w zakresie.',
  },
  {
    id: 'montaz_wc_bialy',
    category: 'hydraulika_montaz',
    label: 'Montaz WC / bialy montaz',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Zakres zalezy od typu urzadzenia, dostepu i odpowiedzialnosci za material.',
  },
  {
    id: 'stelaz_podtynkowy',
    category: 'hydraulika_montaz',
    label: 'Stelaz podtynkowy',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Do potwierdzenia: montaz, zabudowa, izolacja akustyczna i odpowiedzialnosc za stelaz.',
  },
  {
    id: 'montaz_baterii',
    category: 'hydraulika_montaz',
    label: 'Montaz baterii',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Typ baterii, dostep i przerobki instalacji decyduja o stawce.',
  },
  {
    id: 'montaz_umywalki',
    category: 'hydraulika_montaz',
    label: 'Montaz umywalki',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Szafka, blat, syfon i uszczelnienia wymagaja potwierdzenia.',
  },
  {
    id: 'montaz_kabiny',
    category: 'hydraulika_montaz',
    label: 'Montaz kabiny prysznicowej',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Szklo, uszczelnienie i regulacja drzwi powinny byc osobno potwierdzone.',
  },
  {
    id: 'przerobka_odplywu',
    category: 'hydraulika_montaz',
    label: 'Przerobka odplywu',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Zakres zalezy od posadzki, spadkow i dostepu do instalacji.',
  },
  {
    id: 'skuwanie_plytek',
    category: 'przygotowanie',
    label: 'Skuwanie plytek',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Zalezy od podloza, grubosci kleju, wynoszenia i utylizacji.',
  },
  {
    id: 'wynoszenie_gruzu',
    category: 'przygotowanie',
    label: 'Wynoszenie gruzu',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Pozycja do sprawdzenia przy demontazach, pietrach bez windy i dlugim noszeniu.',
  },
  {
    id: 'gruntowanie',
    category: 'przygotowanie',
    label: 'Gruntowanie',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Do decyzji w Tarczy Marzy. Chlonnosc podloza i liczba warstw zmieniaja koszt.',
  },
  {
    id: 'hydroizolacja',
    category: 'przygotowanie',
    label: 'Hydroizolacja',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Potwierdz zakres stref mokrych, tasm, naroznikow i przejsc instalacyjnych.',
  },
  {
    id: 'tasma_narozna_hydro',
    category: 'przygotowanie',
    label: 'Tasma narozna hydro',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Material pomocniczy i robocizna przy naroznikach powinny byc potwierdzone.',
  },
  {
    id: 'zrywanie_tapet',
    category: 'przygotowanie',
    label: 'Zrywanie tapet',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Warstwy tapet, klej i stan podloza zmieniaja czas przygotowania.',
  },
  {
    id: 'demontaz_paneli',
    category: 'przygotowanie',
    label: 'Demontaz paneli',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Demontaz, wyniesienie i utylizacja nie powinny byc ukryte w nowej podlodze.',
  },
  {
    id: 'odpylanie_podloza',
    category: 'przygotowanie',
    label: 'Odpylanie podloza',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Po szlifowaniu i skuwaniu odpylanie jest realna robota do potwierdzenia.',
  },
  {
    id: 'mycie_odtluszczanie',
    category: 'przygotowanie',
    label: 'Mycie / odtluszczanie podloza',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Kuchnie, stare farby i brudne sciany moga wymagac dodatkowego przygotowania.',
  },
  {
    id: 'zabezpieczenie_ciagnikow_komunikacji',
    category: 'przygotowanie',
    label: 'Zabezpieczenie ciagow komunikacji',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Klatki, windy i dojscia do mieszkania warto ustalic przed rozpoczeciem prac.',
  },
  {
    id: 'scianka_gk',
    category: 'gk_murowanie',
    label: 'Scianka GK',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Stawka do wpisania przez wykonawce. Material, profile i trudnosc wymagaja potwierdzenia.',
  },
  {
    id: 'sufit_podwieszany',
    category: 'gk_murowanie',
    label: 'Sufit podwieszany',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Zakres zalezy od poziomow, oswietlenia, profili i wysokosci pracy.',
  },
  {
    id: 'zabudowa_gk_trudna',
    category: 'gk_murowanie',
    label: 'Zabudowa GK trudna',
    unit: 'komplet',
    region: REGION,
    status: 'backlog',
    description: 'Backlog PRO: wycena po szczegolach zabudowy, naroznikow i instalacji.',
  },
  {
    id: 'murowanie_suporex',
    category: 'gk_murowanie',
    label: 'Murowanie Suporex',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Stawka do potwierdzenia przez wykonawce zależnie od grubosci, zaprawy i dostepu.',
  },
  {
    id: 'murowanie_silka',
    category: 'gk_murowanie',
    label: 'Murowanie Silka',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Stawka do potwierdzenia. Ciezar bloczkow i logistyka moga zmienic robocizne.',
  },
  {
    id: 'spoinowanie_gk',
    category: 'gk_murowanie',
    label: 'Spoinowanie plyt GK',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Laczenia, tasmy i ilosc warstw masy trzeba potwierdzic w zakresie.',
  },
  {
    id: 'tasmy_gk',
    category: 'gk_murowanie',
    label: 'Tasmy do plyt GK',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Material pomocniczy do laczen i naroznikow nie powinien znikac z kalkulacji.',
  },
  {
    id: 'narozniki_aluminiowe',
    category: 'gk_murowanie',
    label: 'Narozniki aluminiowe',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Narozniki, docinki i montaz sa pozycja do sprawdzenia.',
  },
  {
    id: 'izolacja_welna_gk',
    category: 'gk_murowanie',
    label: 'Izolacja welna w GK',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Welna, ukladanie i docinki wymagaja decyzji materialowej.',
  },
  {
    id: 'rewizja_gk',
    category: 'gk_murowanie',
    label: 'Rewizja w zabudowie GK',
    unit: 'szt',
    region: REGION,
    status: 'advisory',
    description: 'Rewizje, ramki i docinki powinny byc ustalone przed oferta.',
  },
  {
    id: 'zabudowa_rur_gk',
    category: 'gk_murowanie',
    label: 'Zabudowa rur GK',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Dlugosc, narozniki i dostep do instalacji wymagaja potwierdzenia.',
  },
  {
    id: 'ukladanie_kostki',
    category: 'bruk',
    label: 'Ukladanie kostki',
    unit: 'm2',
    laborRateNet: 95,
    rangeMin: 81,
    rangeMax: 119,
    region: REGION,
    status: 'active',
    description: 'Robocizna ukladania kostki. Podbudowa, obrzeza i transport wymagaja osobnego sprawdzenia.',
  },
  {
    id: 'obrzeza_krawezniki',
    category: 'bruk',
    label: 'Obrzeza / krawezniki',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Stabilizuja nawierzchnie. Potwierdz dlugosc, material i osadzenie.',
  },
  {
    id: 'podbudowa',
    category: 'bruk',
    label: 'Podbudowa',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Grubosc, kruszywo, zageszczenie i warunki gruntu wymagaja decyzji wykonawcy.',
  },
  {
    id: 'podsypka',
    category: 'bruk',
    label: 'Podsypka',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Material i robocizna podsypki sa do potwierdzenia z zakresem prac.',
  },
  {
    id: 'transport_materialu',
    category: 'bruk',
    label: 'Transport materialu',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Nie dolicza sie automatycznie. Sprawdz, kto odpowiada za dostawy.',
  },
  {
    id: 'wywoz_urobku',
    category: 'bruk',
    label: 'Wywoz urobku',
    unit: 'komplet',
    region: REGION,
    status: 'advisory',
    description: 'Do decyzji przy korytowaniu, rozbiorce lub nadmiarze ziemi.',
  },
  {
    id: 'korytowanie',
    category: 'bruk',
    label: 'Korytowanie',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Glebokosc, grunt i dostep sprzetu zmieniaja koszt pracy.',
  },
  {
    id: 'geowloknina',
    category: 'bruk',
    label: 'Geowloknina',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Material pomocniczy do stabilizacji warstw, do potwierdzenia w zakresie.',
  },
  {
    id: 'docinki_kostki',
    category: 'bruk',
    label: 'Docinki kostki',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Luki, obrzeza, studzienki i skosy podbijaja ilosc docinek.',
  },
  {
    id: 'zageszczenie_warstw',
    category: 'bruk',
    label: 'Zageszczenie warstw',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Zageszczarka, liczba warstw i dojazd sprzetu wymagaja ustalenia.',
  },
  {
    id: 'piasek_fugowy',
    category: 'bruk',
    label: 'Piasek fugowy',
    unit: 'm2',
    region: REGION,
    status: 'advisory',
    description: 'Material i zasypanie spoin traktuj jako osobna pozycje pomocnicza.',
  },
  {
    id: 'odwodnienie_liniowe',
    category: 'bruk',
    label: 'Odwodnienie liniowe',
    unit: 'mb',
    region: REGION,
    status: 'advisory',
    description: 'Spadki, kanal i montaz odwodnienia wymagaja indywidualnego potwierdzenia.',
  },
];

export const WORKING_RATE_CATEGORY_LABELS: Record<WorkingRateCategory, string> = {
  malarz: 'Malarz',
  plytki: 'Plytki / gres',
  lazienka: 'Lazienka orientacyjnie',
  hydraulika_montaz: 'Hydraulika / montaz',
  przygotowanie: 'Przygotowanie',
  gk_murowanie: 'GK / murowanie',
  bruk: 'Bruk',
};

export const WORKING_RATE_STATUS_LABELS: Record<WorkingRateStatus, string> = {
  active: 'aktywne',
  advisory: 'do sprawdzenia',
  backlog: 'backlog',
};

export function formatWorkingRateUnit(unit: WorkingRateUnit): string {
  if (unit === 'm2') return 'm²';
  if (unit === 'percent') return '%';
  return unit;
}

export function formatWorkingRateValue(item: WorkingRateItem): string {
  const unit = formatWorkingRateUnit(item.unit);
  const suffix = item.unit === 'percent' ? 'robocizny' : `/${unit}`;

  if (item.laborRateNet !== undefined && item.helperMaterialRateNet !== undefined) {
    return `${item.laborRateNet} zł/${unit} + materiał pomocniczy ${item.helperMaterialRateNet} zł`;
  }

  if (item.laborRateNet !== undefined) {
    return `${item.laborRateNet} zł/${unit}`;
  }

  if (item.rangeMin !== undefined && item.rangeMax !== undefined) {
    return `${item.rangeMin}-${item.rangeMax}${item.unit === 'percent' ? '%' : ` zł/${unit}`} ${item.unit === 'percent' ? suffix : ''}`.trim();
  }

  return 'do decyzji wykonawcy';
}

export function getWorkingRateItemsForQuickScope(scope: QuickWorkingRateScope): WorkingRateItem[] {
  if (scope === 'paving') {
    return pickWorkingRateItems([
      'ukladanie_kostki',
      'obrzeza_krawezniki',
      'podbudowa',
      'podsypka',
      'korytowanie',
      'geowloknina',
      'docinki_kostki',
      'zageszczenie_warstw',
      'transport_materialu',
      'wywoz_urobku',
    ]);
  }

  if (scope === 'bathroom') {
    return pickWorkingRateItems([
      'gres_standard',
      'jolly_45',
      'otwor_w_gresie',
      'hydroizolacja',
      'tasma_narozna_hydro',
      'punkt_hydrauliczny',
      'fugowanie',
      'silikonowanie_lazienka',
      'system_poziomowania',
      'lazienka_deweloperska',
      'lazienka_generalny_remont',
    ]);
  }

  if (scope === 'plaster_painting' || scope === 'scrape_prime_plaster_paint') {
    return pickWorkingRateItems([
      'gladz_obrobka_szlif',
      'malowanie_2x',
      'gladz_malowanie',
      'materialy_pomocnicze_malarz',
      'material_glowny_standard',
      'material_glowny_premium',
      'gruntowanie',
      'zabezpieczenie_folia_tasma',
      'akrylowanie_polaczen',
      'naprawa_ubytkow_malarz',
    ]);
  }

  if (scope === 'painting') {
    return pickWorkingRateItems([
      'malowanie_2x',
      'materialy_pomocnicze_malarz',
      'material_glowny_standard',
      'material_glowny_premium',
      'gruntowanie',
      'zabezpieczenie_folia_tasma',
      'akrylowanie_polaczen',
      'malowanie_sufitu',
    ]);
  }

  return pickWorkingRateItems([
    'malowanie_2x',
    'gres_standard',
    'ukladanie_kostki',
    'punkt_hydrauliczny',
    'scianka_gk',
    'wynoszenie_gruzu',
  ]);
}

function pickWorkingRateItems(ids: string[]): WorkingRateItem[] {
  const byId = new Map(WORKING_RATE_BOOK_V1.map((item) => [item.id, item]));
  return ids.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
}
